'use client';

import React, { useEffect, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { Drawer } from "./Drawer";
import { useAccountsCtx, errText } from "../internals";
import { useToast } from "./Toaster";

const blank = { name: "", contact_person: "", phone: "", email: "", city: "", value: "", status: "active", notes: "" };

export const AccountDrawer = ({ open, account, onClose, onChanged }) => {
  const { storage, currentUser } = useAccountsCtx();
  const toast = useToast();
  const isEdit = !!account;
  const canDelete = ["owner", "admin"].includes(currentUser.role);

  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(
        account
          ? {
              name: account.name || "", contact_person: account.contact_person || "",
              phone: account.phone || "", email: account.email || "", city: account.city || "",
              value: account.value ?? "", status: account.status || "active", notes: account.notes || "",
            }
          : blank
      );
      setErr(null);
      setConfirmDelete(false);
    }
  }, [open, account]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return setErr("Please enter the customer's name.");
    setErr(null);
    setSaving(true);
    const body = {
      ...form,
      value: form.value === "" ? null : Number(form.value),
    };
    try {
      if (isEdit) await storage.updateAccount(account.id, body);
      else await storage.createAccount(body);
      toast.show(isEdit ? "Customer updated." : "Customer added.", "success");
      onChanged && onChanged();
      onClose();
    } catch (e) {
      const m = errText(e);
      setErr(m);
      toast.show(m, "error");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await storage.deleteAccount(account.id);
      toast.show("Customer removed.", "success");
      onChanged && onChanged();
      onClose();
    } catch (e) {
      toast.show(errText(e), "error");
      setDeleting(false);
    }
  };

  const inputCls =
    "w-full rounded-md border border-zinc-300 px-3 py-2.5 text-sm outline-none focus:border-zinc-900";

  return (
    <Drawer
      open={open}
      onClose={() => !saving && !deleting && onClose()}
      testId="account-drawer"
      title={isEdit ? form.name || "Customer" : "Add a customer"}
      subtitle={isEdit ? "Edit this customer's details" : "A company or customer you've won"}
      footer={
        <div className="flex gap-2">
          <button type="button" data-testid="account-cancel-btn" onClick={() => !saving && onClose()} disabled={saving}
            className="flex-1 rounded-md border border-zinc-300 px-4 py-2.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50">
            Cancel
          </button>
          <button type="button" data-testid="account-save-btn" onClick={save} disabled={saving}
            className="flex flex-1 items-center justify-center gap-2 rounded-md bg-green-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-60">
            {saving && <Loader2 size={15} className="animate-spin" />}
            {isEdit ? "Save changes" : "Add customer"}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-zinc-700">Customer / company name</label>
          <input data-testid="account-name-input" value={form.name} onChange={(e) => set("name")(e.target.value)} autoFocus placeholder="e.g. Acme Builders" className={inputCls} />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-zinc-700">Contact person <span className="font-normal text-zinc-400">(optional)</span></label>
          <input data-testid="account-contact-input" value={form.contact_person} onChange={(e) => set("contact_person")(e.target.value)} placeholder="Who you deal with" className={inputCls} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-zinc-700">Phone</label>
            <input data-testid="account-phone-input" value={form.phone} onChange={(e) => set("phone")(e.target.value.replace(/\D/g, "").slice(0, 10))} inputMode="numeric" placeholder="10-digit" className={inputCls} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-zinc-700">City</label>
            <input data-testid="account-city-input" value={form.city} onChange={(e) => set("city")(e.target.value)} className={inputCls} />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-zinc-700">Email <span className="font-normal text-zinc-400">(optional)</span></label>
          <input data-testid="account-email-input" value={form.email} onChange={(e) => set("email")(e.target.value)} type="email" placeholder="name@company.com" className={inputCls} />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-zinc-700">Value won <span className="font-normal text-zinc-400">(optional, ₹)</span></label>
          <input data-testid="account-value-input" value={form.value} onChange={(e) => set("value")(e.target.value.replace(/[^\d.]/g, ""))} inputMode="decimal" placeholder="e.g. 2500000" className={inputCls} />
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-zinc-700">Status</label>
          <div className="grid grid-cols-2 gap-2">
            {[
              { v: "active", l: "Active" },
              { v: "inactive", l: "Inactive" },
            ].map((s) => (
              <button key={s.v} type="button" data-testid={`account-status-${s.v}`} onClick={() => set("status")(s.v)}
                className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                  form.status === s.v ? "border-rose-600 bg-rose-600 text-white" : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                }`}>
                {s.l}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-zinc-700">Notes <span className="font-normal text-zinc-400">(optional)</span></label>
          <textarea data-testid="account-notes-input" value={form.notes} onChange={(e) => set("notes")(e.target.value)} rows={3} placeholder="Anything worth remembering" className={inputCls} />
        </div>

        {err && <div data-testid="account-error" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}

        {isEdit && canDelete && (
          <div className="border-t border-zinc-200 pt-4">
            {!confirmDelete ? (
              <button type="button" data-testid="account-delete-btn" onClick={() => setConfirmDelete(true)}
                className="flex w-full items-center gap-2 rounded-md border border-red-200 px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50">
                <Trash2 size={15} /> Remove this customer
              </button>
            ) : (
              <div className="rounded-md bg-red-50 px-3 py-3">
                <p className="text-sm text-red-700">Remove {form.name}? This can't be undone.</p>
                <div className="mt-2 flex gap-2">
                  <button type="button" onClick={() => setConfirmDelete(false)} disabled={deleting}
                    className="flex-1 rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50">
                    Cancel
                  </button>
                  <button type="button" data-testid="account-delete-confirm-btn" onClick={doDelete} disabled={deleting}
                    className="flex flex-1 items-center justify-center gap-2 rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60">
                    {deleting && <Loader2 size={14} className="animate-spin" />} Remove
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Drawer>
  );
};
